import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { privateAPI as api } from "@/api/axios";
import { Button, Heading, Loader } from "@/ui";

interface LogEntry {
  timestamp?: string;
  user?: string;
  method?: string;
  path?: string;
  payload?: string;
}

interface ActivityLogTabProps {
  /** Short subject type: 'user' | 'partner' | 'collaborator' */
  subjectType: string;
  subjectId: string;
}

export const ActivityLogTab = ({
  subjectType,
  subjectId,
}: ActivityLogTabProps) => {
  const [refreshKey, setRefreshKey] = useState(0);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["activity-log", subjectType, subjectId, refreshKey],
    queryFn: async () => {
      const response = await api.get<{ data: { logs: LogEntry[] } }>(
        "/system/activity-log",
        {
          params: { subject_type: subjectType, subject_id: subjectId },
        },
      );
      return response.data.data.logs;
    },
    enabled: !!subjectId,
  });

  return (
    <div className="flex flex-col gap-6 pt-4">
      <div className="flex items-center justify-between">
        <Heading level={3}>Activity Log</Heading>
        <Button
          onClick={() => {
            setRefreshKey((prev) => prev + 1);
            void refetch();
          }}
          size="sm"
          variant="outlined"
        >
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div className="border-gray-200 overflow-hidden rounded-lg border bg-white shadow-sm">
          <table className="divide-gray-200 min-w-full divide-y text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-gray-500 px-4 py-2 text-left font-medium">
                  Time
                </th>
                <th className="text-gray-500 px-4 py-2 text-left font-medium">
                  Performed By
                </th>
                <th className="text-gray-500 px-4 py-2 text-left font-medium">
                  Action
                </th>
                <th className="text-gray-500 px-4 py-2 text-left font-medium">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-gray-200 divide-y bg-white">
              {data?.map((log, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="text-gray-500 whitespace-nowrap px-4 py-2 align-top">
                    {log.timestamp}
                  </td>
                  <td className="text-gray-900 whitespace-nowrap px-4 py-2 align-top font-medium">
                    {log.user}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 align-top">
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                        log.method?.includes("DELETE")
                          ? "bg-red-50 text-red-700 ring-red-600/10"
                          : log.method?.includes("Update")
                            ? "bg-blue-50 text-blue-700 ring-blue-600/10"
                            : "bg-green-50 text-green-700 ring-green-600/10"
                      }`}
                    >
                      {log.method} {log.path}
                    </span>
                  </td>
                  <td className="text-gray-600 font-mono whitespace-pre-wrap px-4 py-2 align-top text-sm leading-relaxed">
                    {log.payload}
                  </td>
                </tr>
              ))}
              {data?.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="text-gray-500 px-4 py-4 text-center"
                  >
                    No activity logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
